import type { NextRequest } from 'next/server';

import { SESSION_COOKIE_NAME } from 'constants/session';

/** Path to `/api-app/session`, whose DELETE handler expires the session cookie. */
export const SESSION_COOKIE_ENDPOINT = '/api-app/session';

/**
 * Registrable domains we scope auth cookies to, so they are shared across subdomains
 * (`kalshi.com` ↔ `api.elections.kalshi.com`, `dev.kalshi.co` ↔ `demo-api.kalshi.co`).
 */
const SHARED_COOKIE_DOMAINS = ['.kalshi.com', '.kalshi.co'] as const;

/**
 * The `Domain` an auth cookie should carry on `hostname`, or `null` for a host-only
 * cookie.
 *
 * Derived from the live hostname rather than `frontendEnv`, because the two disagree in
 * the documented `start-prod-local` setup: it serves `prod-local.kalshi.com` with
 * `NEXT_PUBLIC_FRONTEND_ENVIRONMENT=dev`, so an env-derived `.kalshi.co` is a domain the
 * browser rejects for that host — the cookie is silently never written, and a delete
 * aimed at it silently never lands. Hosts outside a Kalshi registrable domain
 * (`localhost`, `*.vercel.app`) get a host-only cookie, which is what
 * `NEXT_PUBLIC_USE_LOCAL_COOKIES` used to arrange by hand.
 */
export function getSharedCookieDomain(hostname: string): string | null {
    return SHARED_COOKIE_DOMAINS.find((domain) => hostname === domain.slice(1) || hostname.endsWith(domain)) ?? null;
}

/**
 * `Set-Cookie` values that expire the session cookie for a request to `hostname`.
 *
 * Two of them: the domain-scoped cookie the exchange API issues, and a host-only
 * fallback for environments where it was set without a `Domain` attribute. Cookies are
 * keyed by (name, domain, path), so each variant needs its own delete — the same reason
 * `removeCookie` writes two.
 *
 * No `Secure` and no `HttpOnly`: neither participates in the key, so leaving them off
 * still overwrites an `HttpOnly; Secure` cookie while keeping this working over plain
 * http on localhost.
 */
export function buildSessionCookieExpiryHeaders(hostname: string): string[] {
    const expired = `${SESSION_COOKIE_NAME}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    const domain = getSharedCookieDomain(hostname);

    return domain ? [`${expired}; Domain=${domain}`, expired] : [expired];
}

/**
 * Whether a request carries a signed-in session cookie: the exchange's
 * `sessions`, or the `userId` the client writes at login. A signed-out visitor
 * can still present a stale one (see `proxy.ts`), so this picks a page or data
 * variant; it never authorizes anything.
 */
export function hasSessionCookie(request: Pick<NextRequest, 'cookies'>): boolean {
    return Boolean(request.cookies.get(SESSION_COOKIE_NAME)?.value) || Boolean(request.cookies.get('userId')?.value);
}
